import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Top-of-screen banner shown while the platform super admin is signed in
 * as another club's owner. Visible on every screen so there's no chance
 * of forgetting which identity is active.
 *
 * Visually loud on purpose (purple/violet so it doesn't get confused with
 * an error banner) and offers a single "Stop impersonating" action that
 * restores the original super-admin session.
 */
export default function ImpersonationBanner() {
  const { impersonationContext, stopImpersonating } = useAuth();
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState('');

  if (!impersonationContext) return null;

  const { targetClubName, targetUserEmail, targetDisplayName } = impersonationContext;
  // Headline = club name (club-scope) > display name (user-scope) > email.
  // Subtext clarifies the underlying identity so it's always obvious who
  // we're acting as even if the headline is just a club label.
  const headline = targetClubName || targetDisplayName || targetUserEmail || 'Unknown';
  const showSubtext = targetClubName ? targetUserEmail : (targetDisplayName ? targetUserEmail : null);

  async function handleStop() {
    setStopping(true);
    setError('');
    try {
      await stopImpersonating();
      // Hard reload so every screen re-fetches with the super-admin's
      // identity instead of relying on stale memberships / hooks that
      // captured the impersonated user.
      window.location.reload();
    } catch (err) {
      setStopping(false);
      setError(err.message || 'Could not stop impersonating');
    }
  }

  return (
    <div style={wrap} role="status" aria-label="Impersonating club admin">
      <div style={card}>
        <div style={iconWrap} aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {/* Eye icon — "you are being watched / watching" */}
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={title}>Signed in as {headline}</div>
          <div style={body}>
            {showSubtext
              ? <>Acting as {showSubtext}. Actions you take are recorded under their identity.</>
              : <>Actions you take are recorded under their identity.</>}
            {error && <div style={errorLine}>{error}</div>}
          </div>
        </div>
        <button
          onClick={handleStop}
          disabled={stopping}
          style={{
            ...stopBtn,
            opacity: stopping ? 0.6 : 1,
            cursor: stopping ? 'wait' : 'pointer',
          }}
        >
          {stopping ? 'Stopping…' : 'Stop'}
        </button>
      </div>
    </div>
  );
}

const wrap = {
  position: 'fixed', left: 0, right: 0, top: 0,
  zIndex: 9100, // above CookieBanner (9000) and SmartAppBanner (99999 is excessive but native-only)
  paddingTop: 'env(safe-area-inset-top, 0px)',
  paddingLeft: 8, paddingRight: 8,
  pointerEvents: 'none',
};

const card = {
  pointerEvents: 'auto',
  margin: '6px auto 0',
  maxWidth: 600,
  display: 'flex', alignItems: 'center', gap: 12,
  // Purple/violet so the banner is unmistakable and doesn't blend with
  // accent-red error or success-green tones used elsewhere.
  background: 'linear-gradient(90deg, #6b21a8 0%, #7c3aed 100%)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 12,
  padding: '10px 12px',
  boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
};

const iconWrap = {
  width: 30, height: 30, borderRadius: 8,
  background: 'rgba(255,255,255,0.18)',
  color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

const title = {
  fontSize: '0.875rem', fontWeight: 700, lineHeight: 1.2,
};

const body = {
  fontSize: '0.75rem', lineHeight: 1.35,
  color: 'rgba(255,255,255,0.85)',
  marginTop: 2,
};

const errorLine = {
  marginTop: 4,
  fontSize: '0.6875rem',
  color: '#fecaca',
};

const stopBtn = {
  padding: '8px 14px', fontSize: '0.8125rem', fontWeight: 700,
  background: 'rgba(255,255,255,0.18)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.35)',
  borderRadius: 8,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
